/**
 * Steer a run from its row (core-loop benchmark): `r` on any run row, or the row's Steer button
 * on phones, opens this one-line composer in place — no navigation, no dialog. One sentence and
 * `⏎` sends it: the agent reads it at its next step. `⌘⏎` sends it after this step instead.
 * `Esc` closes and returns focus to the row. Stop run is apart from the send actions.
 */
import { Send, Square } from 'lucide-react'
import { useEffect, useId, useRef, useState } from 'react'

/** `send`: delivered at the next safe point (steers a busy run); `later`: after this step. */
export type SteerMode = 'send' | 'later'

export function SteerBar({
  title,
  agent,
  busy,
  onSend,
  onStop,
  onClose,
}: {
  title: string
  /** "Claude" / "Codex": who reads the message. */
  agent: string
  /** The run is mid-turn: "Send after this step" is offered. */
  busy: boolean
  /** Resolves true when the message was accepted (the bar then closes). */
  onSend: (text: string, mode: SteerMode) => Promise<boolean>
  onStop: () => void
  onClose: () => void
}) {
  const id = useId()
  const field = useRef<HTMLTextAreaElement>(null)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  useEffect(() => {
    field.current?.focus()
  }, [])
  const send = async (mode: SteerMode) => {
    const t = text.trim()
    if (!t || sending) return
    setSending(true)
    try {
      if (await onSend(t, mode)) {
        setText('')
        onClose()
      }
    } finally {
      setSending(false)
    }
  }
  return (
    <div
      className="tr-steer"
      role="group"
      aria-labelledby={`${id}-l`}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <label id={`${id}-l`} htmlFor={`${id}-f`} className="sr-only">
        Steer {title}
      </label>
      <textarea
        id={`${id}-f`}
        ref={field}
        rows={1}
        className="tr-steer__field"
        value={text}
        disabled={sending}
        placeholder={
          busy
            ? `Tell ${agent} what to change… it reads it at the next step`
            : `Tell ${agent} what to change…`
        }
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault()
            onClose()
            return
          }
          if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return
          e.preventDefault()
          void send(e.metaKey || e.ctrlKey ? 'later' : 'send')
        }}
      />
      <div className="tr-steer__row">
        <button
          type="button"
          className="vk-btn vk-btn--primary vk-btn--sm"
          disabled={!text.trim() || sending}
          onClick={() => void send('send')}
        >
          <Send size={13} aria-hidden="true" />
          <span>Send</span>
          <kbd>⏎</kbd>
        </button>
        {busy ? (
          <button
            type="button"
            className="vk-btn vk-btn--secondary vk-btn--sm"
            disabled={!text.trim() || sending}
            onClick={() => void send('later')}
          >
            <span>Send after this step</span>
            <kbd>⌘⏎</kbd>
          </button>
        ) : null}
        <span className="tr-grow" />
        <button
          type="button"
          className="vk-btn vk-btn--ghost vk-btn--sm"
          aria-label={`Stop run ${title}`}
          onClick={onStop}
        >
          <Square size={13} aria-hidden="true" />
          <span>Stop run</span>
        </button>
        <button type="button" className="vk-btn vk-btn--ghost vk-btn--sm" onClick={onClose}>
          <span>Close</span>
          <kbd>esc</kbd>
        </button>
      </div>
    </div>
  )
}
